import CardRadioGroup from "#components/CardRadioGroup/index.js";
import DropdownSelect from "#components/DropdownSelect/index.js";
import MDButton from "#components/MDButton/index.js";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Cancel, CheckCircle, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  CircularProgress as MuiCircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import Deleted from "components/Deleted";
import MDBox from "components/MDBox";
import NoScrollDialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { formatDateTimeFromISOStringUTC } from "layouts/audits/components/utils";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import entityElementsService from "services/entity-elements-service";
import sheetsService from "services/sheets-service";
import useObjectState from "utils/useObjectState";

function evaluateRule(rule, inputValue) {
  if (!rule) return {};
  const conditions = rule.sheetRuleConditions || [];

  const defaultCondition = conditions.find(
    (c) => c.isDefault === true && c.isActive,
  );
  const standardConditions = conditions.filter(
    (c) => c.isDefault === false && c.isActive,
  );

  const evaluateClause = (input, operator, targetValue) => {
    const numInput = Number(input);
    const numTarget = Number(targetValue);
    const isNumeric = !isNaN(numInput) && !isNaN(numTarget);

    const val1 = isNumeric ? numInput : input;
    const val2 = isNumeric ? numTarget : targetValue;

    switch (operator) {
      case ">":
        return val1 > val2;
      case ">=":
        return val1 >= val2;
      case "<":
        return val1 < val2;
      case "<=":
        return val1 <= val2;
      case "=":
      case "==":
      case "===":
        return val1 == val2;
      case "!=":
      case "!==":
        return val1 != val2;
      default:
        return false;
    }
  };

  for (const condition of standardConditions) {
    if (!condition.clauses || condition.clauses.length === 0) continue;

    let isConditionMet = evaluateClause(
      inputValue,
      condition.clauses[0].operator,
      condition.clauses[0].value,
    );

    for (let i = 1; i < condition.clauses.length; i++) {
      const currentClause = condition.clauses[i];
      const prevLogicalOp = condition.clauses[i].logicalOp
        ?.toUpperCase()
        .trim();
      const nextClauseResult = evaluateClause(
        inputValue,
        currentClause.operator,
        currentClause.value,
      );

      if (prevLogicalOp === "OR" || prevLogicalOp === "||") {
        isConditionMet = isConditionMet || nextClauseResult;
      } else {
        isConditionMet = isConditionMet && nextClauseResult;
      }
    }

    if (isConditionMet) {
      return {
        score: condition.result,
        type: condition.type,
        action: condition.action,
      };
    }
  }

  if (defaultCondition) {
    return {
      score: defaultCondition.result,
      type: defaultCondition.type,
      action: defaultCondition.action,
    };
  }

  return null;
}

const CellRenderer = memo(
  ({ property, entityElementId, initialValue, onUpdate, sheetId, answered }) => {
    const rule = property.sheetRule;
    const propertyId = property.id;
    const generic = property.sheetProperty?.generic || "Action";
    const unitName = property.sheetProperty?.unit?.name;
    const options = property.sheetProperty?.options;
    const min = property.sheetProperty?.min;
    const max = property.sheetProperty?.max;

    const theme = useTheme();

    const [localValue, setLocalValue] = useState(initialValue || null);

    let errorMessage = null;
    if (localValue !== null && localValue !== "") {
      if (generic === "number") {
        const numVal = Number(localValue);
        if (!isNaN(numVal)) {
          if (min != null && numVal < min) errorMessage = `Min value is ${min}`;
          if (max != null && numVal > max) errorMessage = `Max value is ${max}`;
        }
      } else if (generic === "text" && max != min) {
        const len = String(localValue).length;
        if (min != null && len < min) errorMessage = `Min length is ${min}`;
        if (max != null && len > max) errorMessage = `Max length is ${max}`;
      }
    }

    useEffect(() => {
      setLocalValue(initialValue || null);
    }, [initialValue]);

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (localValue !== (initialValue || null) && !errorMessage) {
          onUpdate(entityElementId, propertyId, { value: localValue });
        }
      }, 400);

      return () => clearTimeout(timeoutId);
    }, [
      localValue,
      initialValue,
      entityElementId,
      propertyId,
      onUpdate,
      errorMessage,
    ]);

    let content = "";

    if (generic === "number") {
      content = (
        <>
          <TextField
            variant="standard"
            type="number"
            value={localValue || ""}
            onChange={(e) => setLocalValue(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            inputProps={{
              min: min,
              max: max,
            }}
            InputProps={{
              endAdornment: unitName ? (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
                    {unitName}
                  </Typography>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              minWidth: "80px",
            }}
          />
        </>
      );
    }

    if (generic === "boolean") {
      content = (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <CardRadioGroup
            items={[
              { id: 1, label: "Yes" },
              { id: 2, label: "No" },
            ]}
            value={localValue === "true" ? 1 : 2}
            onChange={(newValue) => {
              const updatedBool = newValue?.id === 1;
              if (updatedBool !== (localValue === "true")) {
                setLocalValue(updatedBool ? "true" : "false");
              }
            }}
            sx={{ width: "100px" }}
            itemSx={{
              width: "100%",
              color: `${theme.palette.secondary.main} !important`,
            }}
          />
        </Box>
      );
    }

    if (generic === "dropdown") {
      content = (
        <DropdownSelect
          items={
            options?.map((option) => ({
              id: option.value,
              label: option.name,
            })) || []
          }
          initialValue={localValue || null}
          onChange={(newValue) => {
            if (newValue?.id !== localValue) {
              setLocalValue(newValue?.id || null);
            }
          }}
          sx={{ minWidth: "130px", width: "100%" }}
          inputProps={{
            variant: "standard",
            size: "small",
          }}
        />
      );
    }

    if (generic === "datetime") {
      content = (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "standard",
                size: "small",
                required: true,
                sx: { minWidth: "200px" },
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (generic === "datetime-auto") {
      content = (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            disabled={true}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "standard",
                size: "small",
                required: true,
                sx: { minWidth: "200px" },
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (generic === "audio" || generic === "image") {
      content = (
        <MediaCell
          entityElementId={entityElementId}
          propertyId={propertyId}
          initialValue={initialValue}
          onUpdate={onUpdate}
          generic={generic}
          sheetId={sheetId}
        />
      );
    }

    if (generic === "text") {
      content = (
        <TextField
          multiline
          minRows={2}
          fullWidth
          variant="standard"
          value={localValue || ""}
          onChange={(e) => {
            setLocalValue(e.target.value);
          }}
          error={!!errorMessage}
          helperText={errorMessage}
          // inputProps={{
          //   minLength: min,
          //   maxLength: max,
          // }}
          sx={{ minWidth: "200px" }}
        />
      );
    }

    const evaluation = evaluateRule(rule, localValue);

    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
          }}
        >
          {content}
          {evaluation.type && answered ? (evaluation?.type == 1 ? (
            <CheckCircle color="success" />
          ) : (
            <Cancel color="error" />
          )) : null}
        </Box>
        {evaluation.type == 2 && answered ? (
          <Typography sx={{ fontSize: "14px" }}>
            {evaluation?.action}
          </Typography>
        ) : null}
      </Box>
    );
  },
);

const MediaCell = memo(
  ({
    entityElementId,
    propertyId,
    initialValue,
    onUpdate,
    generic,
    sheetId,
  }) => {
    const [uploading, setUploading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const inputRef = useRef(null);

    const isImage = generic === "image";

    const handleFileSelect = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      e.target.value = null;

      setUploading(true);
      const [response, data] = await sheetsService.answerSheetWithFiles(
        sheetId,
        entityElementId,
        propertyId,
        [file],
        {},
      );
      setUploading(false);

      if (response.ok && data?.value) {
        onUpdate(entityElementId, propertyId, { value: data.value });
      }
    };

    const handleRemove = async (e) => {
      e.stopPropagation();
      await sheetsService.answerSheet(sheetId, entityElementId, propertyId, {
        value: null,
      });
      onUpdate(entityElementId, propertyId, { value: null });
    };

    const uploadButton = (
      <span
        onClick={() => inputRef.current?.click()}
        style={{ cursor: "pointer" }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={isImage ? "image/*" : "audio/*"}
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        {uploading ? (
          <Box
            sx={{
              height: "60px",
              width: "60px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <MuiCircularProgress size={20} />
          </Box>
        ) : (
          <ButtonBase
            sx={(theme) => ({
              height: "60px",
              width: "60px",
              overflow: "hidden",
              borderRadius: "8px",
              position: "relative",
              backgroundColor: "whitesmoke",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            })}
          >
            <Icon>{isImage ? "image" : "mic"}</Icon>
          </ButtonBase>
        )}
      </span>
    );

    if (initialValue) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <NoScrollDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                m: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {isImage ? "Image" : "Audio"}
              <IconButton
                aria-label="close"
                onClick={() => setDialogOpen(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
              }}
            >
              {isImage ? (
                <img
                  src={initialValue}
                  alt="Attachment"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <audio controls src={initialValue} style={{ width: "100%" }} />
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDialogOpen(false)}
                variant="text"
                color="info"
              >
                Close
              </Button>
            </DialogActions>
          </NoScrollDialog>

          <ButtonBase
            onClick={() => setDialogOpen(true)}
            sx={(theme) => ({
              height: "60px",
              width: "60px",
              overflow: "hidden",
              borderRadius: "8px",
              border: "1px solid " + theme.palette.divider,
              position: "relative",
            })}
          >
            {!loaded && isImage ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <MuiCircularProgress size={20} color="info" />
              </Box>
            ) : null}

            <Tooltip title="Remove">
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "red",
                  borderRadius: "2px",
                  zIndex: 1,
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    width: "20px",
                    height: "20px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(e);
                  }}
                >
                  <Close sx={{ fontSize: 12, color: "white" }} />
                </IconButton>
              </Box>
            </Tooltip>

            {isImage ? (
              <img
                src={initialValue}
                alt=""
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  display: loaded ? "block" : "none",
                }}
                onLoad={() => setLoaded(true)}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                  backgroundColor: "whitesmoke",
                }}
              >
                <Icon color="info" sx={{ fontSize: 32 }}>
                  audiotrack
                </Icon>
              </Box>
            )}
          </ButtonBase>
        </Box>
      );
    }

    return <Box>{uploadButton}</Box>;
  },
);

export default function Sheet({ setRoute, permissions }) {
  const { label } = useLanguage();
  const { id } = useParams();
  const can = usePermissionMonitor();

  const [sheet, setSheet] = useState(null);
  const [open, changeOpen] = useObjectState({});
  const [options, changeOptions] = useObjectState({});
  const [loading, changeLoading] = useObjectState({});
  const [sheetState, setSheetState] = useState(null);

  const pendingUpdatesCount = useRef(0);

  async function fetch(cancelToken) {
    changeLoading("fetch", true);
    const [response, data] = await sheetsService.getSheet(id, { cancelToken });

    if (response.ok) {
      if (!data) {
        setSheetState("deleted");
      } else {
        setSheet(data);
        setRoute(["logsheets", data.name]);
        setSheetState("ok");
      }
    } else {
      console.log(`Error while fetching sheet: ${data?.error}`);
    }
    changeLoading("fetch", false);
    return data;
  }

  const handleUpdateSheetItem = useCallback(
    async (
      sheetTemplateEntityElementId,
      sheetTemplateSheetPropertyId,
      updates,
    ) => {
      setSheet((prevSheet) => {
        const sheetItems = [...prevSheet.sheetItems];
        const index = sheetItems.findIndex(
          (sheetItem) =>
            sheetItem.sheetTemplateEntityElementId ===
              sheetTemplateEntityElementId &&
            sheetItem.sheetTemplateSheetPropertyId ===
              sheetTemplateSheetPropertyId,
        );

        if (index === -1) {
          sheetItems.push({
            sheetTemplateEntityElementId,
            sheetTemplateSheetPropertyId,
            ...updates,
          });
        } else {
          sheetItems[index] = { ...sheetItems[index], ...updates };
        }

        console.log("SHEET: ", { ...prevSheet, sheetItems });
        return { ...prevSheet, sheetItems };
      });

      pendingUpdatesCount.current += 1;
      changeLoading("answer", true);
      let [response, data] = await sheetsService.answerSheet(
        sheet.id,
        sheetTemplateEntityElementId,
        sheetTemplateSheetPropertyId,
        { value: updates.value },
      );

      pendingUpdatesCount.current -= 1;

      if (response.ok && pendingUpdatesCount.current == 0) {
        [response, data] = await sheetsService.getSheet(sheet.id);
        if (response.ok) {
          const newSheet = data;
          setSheet(newSheet);
        }
      }

      if (pendingUpdatesCount.current === 0) {
        changeLoading("answer", false);
      }
    },
    [sheet?.id],
  );

  async function addEntityElement(v) {
    let [response, data] = await sheetsService.addEntityItem(sheet.id, {
      entityElementId: v?.id,
      name: v?.name,
    });
    if (response.ok) {
      [response, data] = await sheetsService.getSheet(sheet.id);
      if (response.ok) {
        setSheet(data);
      }
    }
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    fetch(source.token);
    return () => {
      source.cancel("Component unmounted");
    };
  }, []);

  const activeProperties = useMemo(
    () =>
      (sheet?.sheetTemplate?.sheetTemplateSheetProperties || []).filter(
        (p) => p.selected !== false,
      ),
    [sheet?.sheetTemplate?.sheetTemplateSheetProperties],
  );
  const activeEntityElements = useMemo(
    () =>
      (sheet?.sheetTemplate?.sheetTemplateEntityElements || []).filter(
        (e) => e.selected !== false,
      ),
    [sheet?.sheetTemplate?.sheetTemplateEntityElements],
  );

  const score = useMemo(() => {
    let score = 0;
    activeEntityElements.forEach((entityElement) => {
      activeProperties.forEach((property) => {
        if (property?.sheetRule?.enabled) {
          const sheetItem = sheet?.sheetItems?.find(
            (item) =>
              item.sheetTemplateEntityElementId === entityElement.id &&
              item.sheetTemplateSheetPropertyId === property.id,
          );
          if (sheetItem) {
            score += Number(
              evaluateRule(property?.sheetRule, sheetItem.value).score,
            );
          }
        }
      });
    });
    return score;
  }, [activeEntityElements, activeProperties, sheet?.sheetItems]);

  if (sheetState === "deleted") {
    return <Deleted item="sheet" />;
  }

  if (loading.fetch || !sheet) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 145px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const templateTypeName =
    sheet.sheetTemplate.type == 1 ? "Measurable" : "Actionable";

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      <Card
        variant="outlined"
        sx={{ borderRadius: "16px", overflow: "visible" }}
      >
        <MDBox
          sx={(theme) => ({
            px: 3,
            py: 2.5,
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          })}
        >
          <Box>
            <Typography variant="h5" color="white" fontWeight="bold">
              {sheet.name}
            </Typography>
            <Typography
              variant="body2"
              color="white"
              sx={{ opacity: 0.8, mt: 0.5, fontWeight: "400" }}
            >
              {templateTypeName} Sheet
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Chip
              size="small"
              label={
                sheet.statusId == 1 || !sheet.statusId
                  ? "Scheduled"
                  : sheet.statusId == 2
                    ? "In Progress"
                    : "Completed"
              }
              sx={{
                backgroundColor:
                  sheet.statusId == 3
                    ? "success.light"
                    : sheet.statusId == 2
                      ? "warning.light"
                      : "whitesmoke",
                color:
                  sheet.statusId == 3
                    ? "success.dark"
                    : sheet.statusId == 2
                      ? "warning.dark"
                      : "white",
                fontWeight: "bold",
                backdropFilter: "blur(4px)",
              }}
            />
            {loading.answer ? (
              <MuiCircularProgress size={16} sx={{ color: "white" }} />
            ) : null}
          </Box>
        </MDBox>

        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Assigned To
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {sheet.assignedTo?.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Scheduled For
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {sheet.occurrenceId
                  ? formatDateTimeFromISOStringUTC(
                      sheet.occurrenceId?.split?.("|")?.[1],
                    )
                  : "Manual (no schedule)"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Template
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {sheet.sheetTemplate?.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Score
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {score}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Branch
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {sheet?.branch?.branchGroup?.name} {sheet?.branch?.name}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {open.entityElement ? (
        <EntityElementDialog
          sheet={sheet}
          onSubmit={addEntityElement}
          onClose={() => changeOpen("entityElement", false)}
        />
      ) : null}
      <Card
        variant="outlined"
        sx={{ borderRadius: "16px", overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {sheet.sheetTemplate.type != 2 ? "Measurements" : "Actions"}
          </Typography>

          {sheet.sheetTemplate.type == 1 ? (
            <MDButton
              size="small"
              color="info"
              variant="gradient"
              onClick={() => changeOpen("entityElement", true)}
            >
              Add entity element
            </MDButton>
          ) : null}
        </Box>
        <DataTable
          table={{
            columns:
              sheet.sheetTemplate.type == 1
                ? [
                    { Header: "Item", accessor: "name" },
                    ...activeProperties.map((property) => ({
                      Header: property.name,
                      accessor: property.name,
                    })),
                    { Header: "Action", accessor: "action" },
                  ]
                : [{ Header: "Action", accessor: "description" }],
            rows:
              sheet.sheetTemplate.type == 1
                ? activeEntityElements.map((entityElement) => {
                    const row = { name: entityElement.name };

                    activeProperties.forEach((property) => {
                      const sheetItem = sheet.sheetItems.find(
                        (item) =>
                          item.sheetTemplateEntityElementId ===
                            entityElement.id &&
                          item.sheetTemplateSheetPropertyId === property.id,
                      );

                      const isRequired = property.required === true;
                      const isEmpty =
                        !sheetItem?.value &&
                        sheetItem?.value !== 0 &&
                        sheetItem?.value !== false;

                      row[property.name] = (
                        <Box
                          sx={{
                            position: "relative",
                            ...(isRequired && isEmpty
                              ? {
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    borderRadius: "4px",
                                    border: "2px solid",
                                    borderColor: "error.main",
                                    pointerEvents: "none",
                                  },
                                }
                              : {}),
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "40px",
                          }}
                        >
                          {isRequired && isEmpty && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "error.main",
                                position: "absolute",
                                top: -12,
                                right: 4,
                                fontSize: "10px",
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              Required
                            </Typography>
                          )}
                          <CellRenderer
                            property={property}
                            rule={property.sheetRule}
                            entityElementId={entityElement.id}
                            propertyId={property.id}
                            initialValue={sheetItem?.value}
                            type={property.type}
                            generic={
                              property.sheetProperty?.generic || "Action"
                            }
                            unitName={property.sheetProperty?.unit?.name}
                            options={property.sheetProperty?.options}
                            onUpdate={handleUpdateSheetItem}
                            sheetId={sheet.id}
                            answered={sheetItem}
                          />
                        </Box>
                      );
                    });

                    return row;
                  })
                : activeProperties.map((property) => {
                    const sheetItem = sheet.sheetItems.find(
                      (item) =>
                        item.sheetTemplateSheetPropertyId === property.id,
                    );

                    const checked = sheetItem?.value != null;
                    const answered = Boolean(sheetItem);

                    const evaluation = evaluateRule(
                      property?.sheetRule,
                      sheetItem?.value,
                    );

                    return {
                      description: (
                        <FormControlLabel
                          sx={{ display: "flex", flexDirection: "row" }}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: 400, fontSize: 16 }}
                              >
                                {property.name}
                              </Typography>

                              {answered ? (evaluation?.type == 1  ? (
                                <CheckCircle color="success" />
                              ) : (
                                <Cancel color="error" />
                              )) : null}

                              {checked && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ fontWeight: "450" }}
                                >
                                  Completed at{" "}
                                  {formatDateTimeFromISOStringUTC(
                                    sheetItem.value,
                                  )}
                                </Typography>
                              )}
                            </Box>
                          }
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                handleUpdateSheetItem(null, property.id, {
                                  value: e.target.checked
                                    ? new Date().toISOString()
                                    : null,
                                })
                              }
                            />
                          }
                        />
                      ),
                    };
                  }),
          }}
          isSorted={false}
          entriesPerPage={{ defaultValue: 50000 }}
          showTotalEntries={false}
          noEndBorder
        />
      </Card>
    </MDBox>
  );
}

function EntityElementDialog({ sheet, onSubmit, onClose }) {
  const [options, setOptions] = useState({
    entityElements: [],
  });
  const [updates, setUpdates] = useState({});

  async function fetch() {
    let [response, data] = await entityElementsService.getEntityElements({
      sheetTemplateId: sheet.sheetTemplateId,
    });
    if (response.ok) {
      setOptions({ ...options, entityElements: data });
    }
  }

  const dropdownOptions = useMemo(() => {
    return {
      entityElements: options.entityElements
        ?.filter((e) => {
          if (
            sheet.sheetTemplate.sheetTemplateEntityElements.find(
              (v) => v.entityElementId == e.id,
            )
          ) {
            return false;
          }
          return true;
        })
        .map((e) => ({
          id: e.id,
          label: e.name,
        })),
    };
  }, [options, sheet.sheetTemplate]);

  useEffect(() => {
    fetch();
  }, []);

  return (
    <Dialog open={true} onClose={onClose} fullWidth minWidth={"md"}>
      <DialogTitle>Add entity element</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <DropdownSelect
              label="Entity element"
              items={dropdownOptions.entityElements}
              initialValue={updates.id}
              onChange={(e) => {
                setUpdates({
                  ...updates,
                  id: e?.id,
                  name: dropdownOptions.entityElements.find(
                    (en) => en.id == e?.id,
                  )?.label,
                });
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => {
            onSubmit(updates);
            onClose();
          }}
        >
          Submit
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
